import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Phone, Lock, LogIn, Loader2, KeyRound, CheckCircle2, Fingerprint } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import Modal from "@/components/Modal";
import PasswordInput from "@/components/PasswordInput";
import { passkeySupported, savedPasskeyId } from "@/lib/passkey";
import { tr as t, LangPicker } from "@/lib/i18n";

function ForgotModal({ open, onClose, initial }) {
  const [id, setId] = useState(initial);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    setErr("");
    try { setMsg(t((await api.post("/auth/forgot-password", { identifier: id, note })).data.message)); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const close = () => { setMsg(""); setNote(""); onClose(); };
  return (
    <Modal open={open} onClose={close} title={t("نسيت كلمة المرور")} testid="forgot-modal">
      {msg ? (
        <div className="text-center space-y-4" data-testid="forgot-success">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
          <p className="text-lg font-bold">{msg}</p>
          <button onClick={close} className="btn btn-lg btn-dark w-full">{t("حسناً")}</button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-slate-600">{t("أدخل رقم هاتفك وسيتم إبلاغ المدير ليعطيك كلمة مرور جديدة.")}</p>
          <input data-testid="forgot-identifier" className="field" dir="auto" placeholder={t("رقم الهاتف أو اسم المستخدم")} value={id} onChange={(e) => setId(e.target.value)} />
          <input data-testid="forgot-note" className="field" placeholder={t("ملاحظة للمدير (اختياري)")} value={note} onChange={(e) => setNote(e.target.value)} />
          {err && <div className="rounded-xl bg-red-50 text-red-700 p-3 font-bold">{err}</div>}
          <button data-testid="forgot-send" disabled={busy || !id.trim()} onClick={send} className="btn btn-lg btn-blue w-full">{t("إبلاغ المدير")}</button>
        </div>
      )}
    </Modal>
  );
}

export default function Login() {
  const { user, login, loginPasskey } = useAuth();
  const [forgot, setForgot] = useState(false);
  const [phone, setPhone] = useState(savedPasskeyId());
  const [pk, setPk] = useState(false);
  useEffect(() => { passkeySupported().then(setPk); }, []);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  if (user) return <Navigate to={user.role === "driver" ? "/driver" : "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try { await login(phone, password, remember); } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  const bio = async () => {
    setErr("");
    if (!phone.trim()) { setErr(t("اكتب رقم هاتفك أولاً ثم اضغط الدخول بالبصمة")); return; }
    setBusy(true);
    try { await loginPasskey(phone.trim()); } catch (e2) { setErr(e2?.response ? errMsg(e2) : t(e2.message)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-brand relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-brand-light via-brand to-brand-dark" />
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute bottom-0 -right-20 w-72 h-72 rounded-full bg-sky-300/10 blur-3xl" />
      <div className="relative safe-top px-5 pt-4 max-w-md w-full mx-auto"><LangPicker dark /></div>
      <div className="relative px-6 pt-6 max-w-md w-full mx-auto flex flex-col items-center text-center text-white fade-up">
        <img src="/img/logo.png" alt={t("شعار الشركة")} data-testid="company-logo" className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-white p-1.5 object-contain shadow-2xl ring-4 ring-white/30" />
        <h1 className="text-3xl font-black mt-5">{t("المحيط الأطلسي")}</h1>
        <p className="text-white/75">{t("لنقل البضائع والمعدات")}</p>
      </div>
      <div className="relative flex-1 px-5 py-8">
        <form onSubmit={submit} className="max-w-md mx-auto rounded-3xl bg-white/10 backdrop-blur-md border border-white/15 p-5 space-y-4 fade-up" data-testid="login-form">
          <p className="text-white/85 text-center">{t("سجّل الدخول برقم هاتفك أو اسم المستخدم")}</p>
          <label className="relative block">
            <Phone className="w-5 h-5 text-slate-400 absolute top-1/2 -translate-y-1/2 start-4" />
            <input data-testid="login-phone-input" className="field w-full ps-12 border-0" dir="auto" autoComplete="username" autoCapitalize="off"
              placeholder={t("رقم الهاتف أو اسم المستخدم")} aria-label={t("رقم الهاتف أو اسم المستخدم")} value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </label>
          <label className="relative block">
            <Lock className="w-5 h-5 text-slate-400 absolute top-1/2 -translate-y-1/2 start-4 z-10" />
            <PasswordInput testid="login-password-input" className="field ps-12 border-0" autoComplete="current-password"
              placeholder={t("كلمة المرور")} aria-label={t("كلمة المرور")} value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <label className="flex items-center gap-3 text-white font-bold cursor-pointer select-none" data-testid="remember-me-label">
            <input data-testid="remember-me-checkbox" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-6 h-6 accent-white rounded" />
            {t("تذكّرني على هذا الجهاز")}
          </label>
          {err && <div data-testid="login-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 p-4 font-bold">{err}</div>}
          <button data-testid="login-submit-button" disabled={busy} className="btn btn-lg w-full bg-white text-brand hover:bg-brand-soft shadow-xl">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <LogIn className="w-6 h-6" />} {t("تسجيل الدخول")}
          </button>
          {pk && <button type="button" data-testid="biometric-login-btn" onClick={bio} disabled={busy} className="btn btn-lg w-full bg-white/15 border-2 border-white/40 text-white hover:bg-white/25">
            <Fingerprint className="w-7 h-7" />{t("الدخول بالبصمة أو الوجه")}</button>}
          <button type="button" data-testid="forgot-password-btn" onClick={() => setForgot(true)} className="w-full text-center text-white/90 font-bold flex items-center justify-center gap-2 h-11"><KeyRound className="w-5 h-5" />{t("نسيت كلمة المرور؟")}</button>
        </form>
        {forgot && <ForgotModal open={forgot} onClose={() => setForgot(false)} initial={phone} />}
      </div>
    </div>
  );
}
